import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Bot, Settings, FileText } from 'lucide-react'
import type { Tool } from '@/services/tools'
import { NotionToolConfigModal } from './notion-tool-config-modal'

interface NotionToolCardProps {
  tool: Tool
  organizationId: string
  onClick?: () => void
  onConfigure?: () => void
}

export function NotionToolCard({
  tool,
  organizationId,
  onClick,
  onConfigure,
}: NotionToolCardProps) {
  const { t } = useTranslation('tools')
  const [configModalOpen, setConfigModalOpen] = useState(false)

  const handleConfigureClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setConfigModalOpen(true)
  }

  const handleConfigSuccess = () => {
    onConfigure?.()
  }

  return (
    <>
      <Card
        variant="glass"
        className="p-6 cursor-pointer group hover:border-cyan-500/30 transition-all duration-200"
        onClick={onClick}
      >
        {/* Header: Icon + Status */}
        <div className="flex items-start justify-between mb-4">
          {/* Icon */}
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
            <FileText className="w-7 h-7 text-amber-400" />
          </div>

          {/* Status Badge */}
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              tool.is_configured
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-gray-500/20 text-gray-400'
            }`}
          >
            {tool.is_configured ? t('card.configured') : t('card.available')}
          </span>
        </div>

        {/* Name & Description */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-white mb-1 truncate">
            {tool.name}
          </h3>
          <p className="text-sm text-gray-400 line-clamp-2 min-h-[2.5rem]">
            {tool.description || t('notion.description')}
          </p>
        </div>

        {/* Footer: Category + Configure/Agent count */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span className="capitalize text-amber-400">
            {t('categories.productivity')}
          </span>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleConfigureClick}
              className="h-7 px-2 text-xs text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
            >
              <Settings className="w-3.5 h-3.5 mr-1" />
              {t('card.configure')}
            </Button>
            <div className="flex items-center gap-1.5">
              <Bot className="w-3.5 h-3.5" />
              <span>
                {tool.agent_count === 0
                  ? t('card.noAgents')
                  : t('card.agents', { count: tool.agent_count })}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Config Modal */}
      <NotionToolConfigModal
        open={configModalOpen}
        onOpenChange={setConfigModalOpen}
        organizationId={organizationId}
        tool={tool}
        onSuccess={handleConfigSuccess}
      />
    </>
  )
}
